import { SxProps, Theme } from "@mui/material";

/** Covers tbody area only while refetching */
export const activitiesTableLoadingOverlaySx: SxProps<Theme> = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  top: 48,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.72)",
  zIndex: 1,
  borderRadius: "0 0 8px 8px",
};

export const tableContainerSx: SxProps<Theme> = {
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  height: "100%",
  position: "relative",
};

export const tableHeaderCellSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: "nowrap",
  backgroundColor: "#fafafa",
  py: 0.75,
  px: 1.25,
  fontSize: "0.8125rem",
};

export const tableBodyCellSx: SxProps<Theme> = {
  verticalAlign: "middle",
  py: 0.75,
  px: 1.25,
  fontSize: "0.875rem",
  lineHeight: 1.43,
};

/** Header + body for domain/type columns (min width so chips sit in a row) */
export const pillClusterHeaderCellSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: "nowrap",
  backgroundColor: "#fafafa",
  minWidth: 280,
  maxWidth: 420,
  py: 0.75,
  px: 1.25,
  fontSize: "0.8125rem",
};

export const pillClusterBodyCellSx: SxProps<Theme> = {
  verticalAlign: "middle",
  minWidth: 280,
  maxWidth: 420,
  py: 0.75,
  px: 1.25,
  fontSize: "0.875rem",
  lineHeight: 1.43,
};

export const paginationContainerSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "end",
  mt: 0.75,
};

/** TablePagination toolbar: default minHeight 56px consumes vertical space */
export const tablePaginationSx: SxProps<Theme> = {
  border: 0,
  "& .MuiTablePagination-toolbar": {
    minHeight: 40,
    pl: 0,
    pr: 0,
    flexWrap: "wrap",
    gap: 0.5,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.8125rem",
    mb: 0,
    mt: 0,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.8125rem",
  },
};

export const tableWrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
};

/**
 * Pill clusters inside table cells: use inline-flex so the cell sizes from content
 * (block-level flex was stretching to a narrow column width and forcing one chip per row).
 */
export const pillContainerSx: SxProps<Theme> = {
  display: "inline-flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  alignContent: "center",
  gap: 0.5,
};

export const categoryPillSx: SxProps<Theme> = {
  borderRadius: "999px",
  fontWeight: 600,
  backgroundColor: "#eef6ff",
  border: "1px solid #cfe4ff",
  color: "#0f4c81",
};

/** Default gray/neutral chips in domain & type columns */
export const neutralPillSx: SxProps<Theme> = {
  borderRadius: "999px",
  fontWeight: 500,
  flexShrink: 0,
  backgroundColor: "#f5f5f5",
  border: "1px solid #e0e0e0",
  color: "text.primary",
};

/** Domain/type pill when it matches an active filter */
export const neutralPillFilterMatchSx: SxProps<Theme> = {
  backgroundColor: "#e8f4fd",
  border: "1px solid #64b5f6",
  color: "#0d47a1",
  fontWeight: 600,
};

export const filterRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  mb: 1,
  alignItems: "center",
};

export const filterControlSx: SxProps<Theme> = {
  minWidth: 140,
  flex: "1 1 140px",
};

export const filterClearButtonWrapperSx: SxProps<Theme> = {
  flex: "0 0 auto",
  alignSelf: "stretch",
  display: "flex",
  ml: "auto",
};

/**
 * App-wide `.MuiOutlinedInput-input { padding: ... !important }` inflates filter height;
 * tighten inputs for this modal only.
 */
export const compactFilterTextFieldSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.8125rem",
  },
  "& .MuiOutlinedInput-input": {
    py: "7px !important",
    px: "11px !important",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.8125rem",
  },
};

export const tableScrollAreaSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

/** Narrow column for per-row add (+) control; extra pl so the toggle is not flush to the table edge */
export const addActivityHeaderCellSx: SxProps<Theme> = {
  ...tableHeaderCellSx,
  width: 52,
  maxWidth: 52,
  minWidth: 52,
  pl: 1.5,
  pr: 1,
  textAlign: "center",
};

export const addActivityBodyCellSx: SxProps<Theme> = {
  width: 52,
  maxWidth: 52,
  minWidth: 52,
  pl: 1.5,
  pr: 1,
  verticalAlign: "middle",
  textAlign: "center",
};

/** Compact +/- control: same size in table and selected list */
const planToggleButtonSizeSx: SxProps<Theme> = {
  width: 28,
  height: 28,
  minWidth: 28,
  minHeight: 28,
  p: 0,
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/** + Add to plan: filled primary so it reads clearly vs disabled */
export const planAddButtonEnabledSx: SxProps<Theme> = {
  ...planToggleButtonSizeSx,
  color: "primary.contrastText",
  bgcolor: "primary.main",
  border: "1px solid",
  borderColor: "primary.main",
  "&:hover": { bgcolor: "primary.dark", borderColor: "primary.dark" },
};

/** + when plan is full and this row is not in the plan */
export const planAddButtonBlockedSx: SxProps<Theme> = {
  ...planToggleButtonSizeSx,
  color: "action.disabled",
  bgcolor: "action.disabledBackground",
  border: "1px solid",
  borderColor: "divider",
  "&.Mui-disabled": {
    color: "action.disabled",
    borderColor: "divider",
    bgcolor: "grey.200",
  },
};

/** − Remove from plan (table row already selected, or selected list) */
export const planRemoveButtonSx: SxProps<Theme> = {
  ...planToggleButtonSizeSx,
  color: "error.main",
  bgcolor: "transparent",
  border: "1px solid",
  borderColor: "error.light",
  "&:hover": {
    bgcolor: "rgba(211, 47, 47, 0.08)",
    borderColor: "error.main",
  },
};

/** Remove control in selected-activities list rows (align with multi-line grid cells) */
export const planRemoveButtonInSelectedListSx: SxProps<Theme> = {
  ...planRemoveButtonSx,
  alignSelf: "center",
};

export const clearAllFiltersButtonSx: SxProps<Theme> = {
  height: 32,
  minHeight: 32,
  px: 1.25,
  borderRadius: "4px",
  fontSize: "0.8125rem",
  lineHeight: 1.15,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  bgcolor: "common.black",
  color: "common.white",
  borderColor: "common.black",
  "&:hover": {
    bgcolor: "grey.900",
    borderColor: "grey.900",
  },
  "&.Mui-disabled": {
    color: "rgba(255, 255, 255, 0.6)",
    bgcolor: "grey.700",
    borderColor: "grey.700",
  },
};

export const planToggleGlyphSx: SxProps<Theme> = {
  fontSize: "0.9375rem",
  fontWeight: 700,
  lineHeight: "28px",
  display: "block",
  textAlign: "center",
  width: "100%",
};
